import { API_URL } from '../config/api';

export async function fetchUsers(token) {
  const response = await fetch(`${API_URL}/api/admin/users`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  const data = await response.json();
  return data.data;
}

export async function fetchUserProfiles(token) {
  const response = await fetch(`${API_URL}/api/admin/profiles`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error('Failed to fetch user profiles');
  }
  const data = await response.json();
  return data.data;
}

export async function fetchUsersWithProfiles(token) {
  try {
    // Fetch both users and profiles in parallel
    const [users, profiles] = await Promise.all([
      fetchUsers(token),
      fetchUserProfiles(token).catch(() => []) // Fallback to empty array if profiles endpoint doesn't exist
    ]);

    // Merge profiles into users - profile data takes priority
    return users.map(user => {
      const profile = profiles.find(p => p.user_id === user.id);
      return {
        ...user,
        // Profile fields take priority over user metadata
        full_name: profile?.full_name || user.user_metadata?.full_name || null,
        email: profile?.email || user.email,
        phone: profile?.phone || user.user_metadata?.phone || user.phone || null,
        // Additional profile fields
        address: profile?.address || null,
        city: profile?.city || null,
        country: profile?.country || null,
        gender: profile?.gender || null,
        date_of_birth: profile?.date_of_birth || null,
        profile_image_url: profile?.profile_image_url || null,
        // Keep full profile object for detailed views
        profile: profile
      };
    });
  } catch (error) {
    console.error('Error fetching users with profiles:', error);
    // Fallback to just users if profiles fetch fails
    return fetchUsers(token);
  }
}

/**
 * Delete user from Supabase
 * @param {string} userId - The user ID to delete
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response with deletion status
 */
export async function deleteUser(userId, token) {
  try {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete user');
    }

    return {
      success: data.success,
      message: data.message,
      data: data.data,
      error: data.error,
      code: data.code
    };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
} 